import React from 'react';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '../../../../api/hooks';
import { useCampStore } from '../../../../stores';
import { copyToClipboard } from '../../../../utils/clipboard';
import InviteCard from './InviteCard';
import FrontendUrlCard from './FrontendUrlCard';
import CampQr from './CampQr';
import './camp.css';

const INVITE_BASE_URLS = {
    ORG: 'https://admin.solbaumanec.ru',
    STUDENT: 'https://solbaumanec.ru'
};

const getBaseUrl = (type) => INVITE_BASE_URLS[type] || `${window.location.protocol}//${window.location.host}`;

// Страница «Лагерь» (настройки лагеря, новая верстка): инфо + ссылки-приглашения + QR.
export default function CampSettingsPage() {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const campStore = useCampStore();
    const campId = campStore.campId;

    const { data: campInfo, isLoading, error } = useApiQuery(
        ['camp', campId],
        campId ? `/api/camps/admin/camps/${campId}` : null,
        {
            enabled: !!campId,
            onSuccess: (data) => campStore.setCamp(data),
            onError: (err) => {
                if (err.response?.status === 404) campStore.clearCamp();
            },
            retry: false
        }
    );

    const handleFrontendUrlSaved = (updatedCamp) => {
        campStore.setCamp(updatedCamp);
        queryClient.setQueryData(['camp', campId], updatedCamp);
    };

    const handleCopy = async (text) => {
        const success = await copyToClipboard(text);
        enqueueSnackbar(
            success ? 'Ссылка скопирована' : 'Не удалось скопировать',
            { variant: success ? 'success' : 'error' }
        );
    };

    if (isLoading) return <div className="camp-card">Загрузка...</div>;
    if (error || !campId) {
        return (
            <div className="camp-card">
                {error?.response?.status === 404
                    ? 'Лагерь не найден. Пожалуйста, перезайдите в систему.'
                    : 'Ошибка при загрузке информации о лагере'}
            </div>
        );
    }

    const orgInviteUrl = campInfo?.orgInviteToken
        ? `${getBaseUrl('ORG')}/invite/${campInfo.orgInviteToken}`
        : '';
    const studentJoinUrl = campInfo?.code
        ? `${getBaseUrl('STUDENT')}/join/${campInfo.code}`
        : '';

    return (
        <>
            <h1 className="camp-title">Настройки лагеря</h1>

            <section className="camp-card camp-info">
                <div className="camp-infohead"><h3>Информация о лагере</h3></div>
                <div className="camp-inforow">
                    <div className="camp-fact">
                        <div className="k">Название лагеря</div>
                        <div className="v">{campInfo?.name || '—'}</div>
                    </div>
                    <div className="camp-fact">
                        <div className="k">Код лагеря</div>
                        <div className="v">{campInfo?.code || '—'}</div>
                    </div>
                </div>
            </section>

            <FrontendUrlCard
                campId={campId}
                initialUrl={campInfo?.frontendUrl || ''}
                onSaved={handleFrontendUrlSaved}
            />

            <div className="camp-row">
                <InviteCard
                    title="Приглашение организаторов"
                    lead="Отправьте эту ссылку организаторам для регистрации в лагере"
                    url={orgInviteUrl}
                    onCopy={handleCopy}
                    note="Организаторы смогут зарегистрироваться по этой ссылке"
                />
                <InviteCard
                    title="Приглашение студентов"
                    lead="Ссылка для регистрации студентов в лагере"
                    url={studentJoinUrl}
                    onCopy={handleCopy}
                >
                    {studentJoinUrl && <CampQr value={studentJoinUrl} />}
                </InviteCard>
            </div>
        </>
    );
}
